import {TypeHelper} from "../helpers/typeHelper";
import {Logger} from "~/services/logger";
import {ArrayHelper} from "~/helpers/arrayHelper";
import {TSelfOrGetter} from '~/interfaces/generalInterfaces'

export interface IChangeDetectorCallbacks<T> {
  shouldCompare(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, detector: ChangeDetector<T>): boolean

  isSame(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, detector: ChangeDetector<T>): boolean

  onDifference(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, detector: ChangeDetector<T>): void

  onSame(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, detector: ChangeDetector<T>): void

  onOtherwise(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, detector: ChangeDetector<T>): void
}

export interface IChangeDetectorOptions<T> extends IChangeDetectorCallbacks<T> {
  left: TSelfOrGetter<T>

  right: TSelfOrGetter<T>

  debug?: boolean
}

export default class ChangeDetector<T = any> {
  constructor(private defaultOpts: Partial<IChangeDetectorOptions<T>> = {}) {
    if (!defaultOpts.isSame) {
      defaultOpts.isSame = this._isSame.bind(this)
    }
  }

  compare(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}): boolean {
    let isSame: boolean = true
    if (this.shouldCompare(left, right, opts)) {
      if (this.isSame(left, right)) {
        this.onSame(left, right, opts)
      } else {
        isSame = false
        this.onDifference(left, right, opts)
      }
    } else {
      this.onOtherwise(left, right, opts)
    }
    return isSame
  }

  onOtherwise(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}): void {
    this._callback('onOtherwise', left, right, opts)
  }

  shouldCompare(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}): boolean {
    return this._callback('shouldCompare', left, right, opts)
  }

  onDifference(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}): void {
    this._callback('onDifference', left, right, opts)
  }

  onSame(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}): void {
    this._callback('onSame', left, right, opts)
  }

  isSame(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}) {
    this.log(`Left: ${this.getValue(left, 'left')}`, `Right: ${this.getValue(right, 'right')}`)
    return this._callback('isSame', left, right, opts)
  }

  private _isSame(left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}) {
    return ChangeDetector.isEqual(left, right)
  }

  private _callback(callbackName: keyof IChangeDetectorCallbacks<T>, left: TSelfOrGetter<T>, right: TSelfOrGetter<T>, opts: Partial<IChangeDetectorOptions<T>> = {}): any {
    const callback = opts[callbackName] || this.defaultOpts[callbackName]
    if (callback) {
      const result = callback(
        this.getValue(left, 'left'),
        this.getValue(right, 'right'),
        this
      )
      this.log(`callback: ${callbackName}`, `result: ${result}`)
      return result
    }
  }

  private getValue(x: TSelfOrGetter<T>, side: 'left' | 'right'): T {
    let getter
    if (TypeHelper.isInstanceOf<(...args) => T>(this.defaultOpts[side], 'function')) {
      getter = this.defaultOpts[side]
    } else {
      getter = (...args) => args[0]
    }
    return getter(TypeHelper.isInstanceOf(x, Function) ? x() : x)
  }

  private log(...messages: string[]) {
    if (this.defaultOpts.debug) {
      Logger.logOnce("Change Detector", ...messages)
    }
  }
  private static isEqual(left: any, right: any): boolean {
    // if val is null or undefined, use null as comparison value
    if (Array.isArray(left) && Array.isArray(right)) {
      // slice and sort before equality check
      return ArrayHelper.equals(left.slice().sort(), right.slice().sort())
    } else {
      return (left ?? null) === (right ?? null)
    }
  }
}